import styled from "@emotion/styled";
import { useState } from "react";

import { ArrowsDiagonal, ArrowsDiagonalMinimize } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Icon,
  Info,
  Tooltip,
  TransitionDurationToken,
} from "@livechat/design-system-react-components";

import { newFeaturesCampaigns } from "constants/campaigns";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { withVar } from "themes/helpers";

interface IProps {
  isCollapsed: boolean;
  isVisible: boolean;
  onClick: () => void;
}

export const CollapseButton: React.FC<IProps> = ({ isCollapsed, isVisible, onClick }) => {
  const { isNewForAgent, featureFlagValue, setAgentFlagCallback } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenCollapseMessageBoxButton.flag,
    newFeaturesCampaigns.hasSeenCollapseMessageBoxButton.campaignStartDate
  );

  const shouldShowTooltip = isNewForAgent && !featureFlagValue;
  const [isTooltipVisible, setIsTooltipVisible] = useState(shouldShowTooltip);

  const handleCloseTooltip = (): void => {
    // optimistic update for instant tooltip closing
    setIsTooltipVisible(false);
    setAgentFlagCallback();
  };

  const handleClick = (): void => {
    onClick();
    if (isTooltipVisible) {
      handleCloseTooltip();
    }
  };

  return (
    <Wrapper>
      <Tooltip
        isVisible={isTooltipVisible && isVisible}
        triggerRenderer={
          <Button
            data-testid="message-box-collapse-button"
            onClick={handleClick}
            css={{
              backgroundColor: withVar(DesignToken.SurfaceSecondaryDefault),
              border: "none",
              "&:hover": {
                backgroundColor: withVar(DesignToken.SurfaceSecondaryHover),
                border: "none",
              },
              "&:active": {
                backgroundColor: withVar(DesignToken.SurfaceSecondaryActive),
                border: "none",
              },
              visibility: isVisible ? "visible" : "hidden",
              opacity: isVisible ? 1 : 0,
              transition: `opacity ${withVar(TransitionDurationToken.Fast2)} ease-in-out, visibility ${withVar(TransitionDurationToken.Fast2)} ease-in-out allow-discrete`,
            }}
            icon={
              <Icon
                source={isCollapsed ? ArrowsDiagonal : ArrowsDiagonalMinimize}
                customColor={withVar(DesignToken.ActionNeutralDefault)}
              />
            }
            kind="secondary"
            size="xcompact"
          />
        }
        portal
        kind="invert"
        placement="top-end"
        css={{
          maxWidth: "270px",
        }}
      >
        <Info
          theme="invert"
          handleCloseAction={handleCloseTooltip}
          header="Adjust message area"
          text="Easily expand or collapse the message area to view more of your reply or the conversation above."
        />
      </Tooltip>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: absolute;
  isolation: isolate;
  top: 0;
  right: -28px;
`;
